import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SOLICITUD_LABELS } from "@/data";
import NotificationBell from "@/components/campus/NotificationBell";
import CourseEditor from "@/components/admin/CourseEditor";
import Bloques from "@/components/admin/Bloques";
import {
  getCourses, adminStats, adminCreateCourse, adminUpdateCourse, adminDeleteCourse, adminReorderCourses,
  getOffer, adminUpdateOffer, adminGetCalls, adminCreateCall, adminUpdateCall, adminDeleteCall,
  adminBookings, adminLeads, adminToggleLead, adminUsers, adminChat, adminDeleteChat, adminDeletePost, getPosts,
  adminAnalytics, uploadFileChunked, fileUrl, adminSetPassword,
  adminEmpleos, adminCreateEmpleo, adminUpdateEmpleo, adminDeleteEmpleo,
  adminRecursos, adminCreateRecurso, adminUpdateRecurso, adminDeleteRecurso, adminReorderRecursos, adminSetAccess, adminCreateUser, adminDeleteUser, adminEditUser, adminResetDevices, adminDeviceRequests, adminApproveDeviceRequest,
  adminGetNotifications, adminSendNotification, adminDeleteNotification,
  adminSolicitudes, adminToggleSolicitud, adminDeleteSolicitud, adminResponderSolicitud,
} from "@/lib/api";

const TABS = ["Resumen", "Analítica", "Cursos", "Oferta", "Llamadas", "Citas", "Leads", "Alumnos", "Empleo", "Recursos", "Solicitudes", "Avisos", "Moderación"];
const fmt = (iso) => { try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };
const fmtDay = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short" }); } catch { return iso; } };
const extTipo = (name) => {
  const e = (name.split(".").pop() || "").toLowerCase();
  if (e === "pdf") return "pdf";
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(e)) return "imagen";
  return "archivo";
};

const reorder = (list, from, to) => {
  const a = [...list];
  const [m] = a.splice(from, 1);
  a.splice(to, 0, m);
  return a;
};

// evita propagar la key booleana `dragging` al DOM
const dragHandlers = (d) => { const { dragging, ...h } = d || {}; return h; };

function Field({ label, ...p }) {
  return <div className="field" style={{ marginBottom: 12 }}><label>{label}</label><input {...p} /></div>;
}

const areaStyle = { width: "100%", background: "var(--ink)", border: "1px solid var(--line)", color: "var(--cream)", padding: "12px 14px", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 13, borderRadius: 8 };

function Uploader({ accept, label, testid, onDone }) {
  const [pct, setPct] = useState(null);
  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPct(0);
    try {
      const res = await uploadFileChunked(file, setPct);
      onDone(res);
      toast.success("Archivo subido ✓");
    } catch (err) {
      toast.error("Error al subir el archivo");
    } finally {
      setPct(null);
      e.target.value = "";
    }
  };
  return (
    <label className="abtn" data-testid={testid} style={{ cursor: pct != null ? "wait" : "pointer", display: "inline-flex", gap: 8, alignItems: "center" }}>
      {pct == null ? label : `Subiendo… ${pct}%`}
      <input type="file" accept={accept} style={{ display: "none" }} onChange={onChange} disabled={pct != null} />
    </label>
  );
}

function LessonEditor({ l, mi, li, count, setLec, delLec, lDrag }) {
  const [open, setOpen] = useState(false);
  const recursos = l.recursos || [];
  const imagenes = l.imagenes || [];
  const videos = l.videos || [];
  const setRec = (list) => setLec(mi, li, "recursos", list);
  const setImgs = (list) => setLec(mi, li, "imagenes", list);
  const setVids = (list) => setLec(mi, li, "videos", list);
  const [dv, setDv] = useState(null);      // drag vídeo
  const [dimg, setDimg] = useState(null);   // drag imagen
  const [dr, setDr] = useState(null);       // drag recurso
  const tid = `${mi}-${li}`;
  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: 8, marginBottom: 8, background: (lDrag && lDrag.dragging) ? "rgba(47,107,255,.10)" : "rgba(255,255,255,0.03)" }} data-testid={`leccion-editor-${tid}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px" }}>
        <span className="drag-handle" title="Arrastra para reordenar" draggable {...dragHandlers(lDrag)}
          data-testid={`leccion-drag-${tid}`} style={{ cursor: "grab", color: "var(--muted)", padding: "0 4px" }}>⠿</span>
        <span style={{ color: "var(--muted)", fontSize: 11, minWidth: 30 }}>{mi + 1}.{li + 1}</span>
        <input value={l.titulo || ""} placeholder={`Título de la lección ${li + 1}`} onChange={(e) => setLec(mi, li, "titulo", e.target.value)}
          style={{ ...areaStyle, flex: 1, padding: "7px 10px" }} data-testid={`leccion-titulo-${tid}`} />
        <button type="button" className="abtn" onClick={() => setOpen((o) => !o)} data-testid={`leccion-toggle-${tid}`}>{open ? "▲" : "▼"}</button>
        <button type="button" className="abtn danger" onClick={() => delLec(mi, li)}>✕</button>
      </div>
      {open && (
        <div style={{ padding: "4px 10px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Submódulo (opcional — agrupa lecciones dentro del módulo)</label>
            <input value={l.submodulo || ""} onChange={(e) => setLec(mi, li, "submodulo", e.target.value)} style={areaStyle} data-testid={`leccion-submodulo-${tid}`} placeholder="Ej: Bloque 1 · Fundamentos" />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Descripción corta</label>
            <input value={l.descripcion || ""} onChange={(e) => setLec(mi, li, "descripcion", e.target.value)} style={areaStyle} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Contenido (lo ve el alumno)</label>
            <textarea value={l.contenido || ""} onChange={(e) => setLec(mi, li, "contenido", e.target.value)} style={{ ...areaStyle, minHeight: 90 }} data-testid={`leccion-contenido-${tid}`} placeholder="Explicación, pasos, notas…" />
          </div>

          {/* ---- Vídeos (varios) ---- */}
          <div>
            <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>Vídeos · añade varios enlaces de YouTube/Vimeo y/o vídeos subidos (arrastra ⠿ para ordenar)</label>
            {videos.map((v, vi) => (
              <div key={vi} draggable data-testid={`leccion-video-item-${tid}-${vi}`}
                onDragStart={() => setDv(vi)} onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dv === null || dv === vi) { setDv(null); return; } setVids(reorder(videos, dv, vi)); setDv(null); }}
                style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, border: "1px solid var(--line)", borderRadius: 8, padding: "6px 8px", background: dv === vi ? "rgba(47,107,255,.08)" : "transparent", cursor: "grab" }}>
                <span style={{ color: "var(--muted)" }}>⠿</span>
                <span style={{ fontSize: 10, textTransform: "uppercase", color: "var(--gold)", minWidth: 52 }}>{v.tipo}</span>
                {v.tipo === "upload" ? (
                  v.file_url ? <span style={{ fontSize: 12, color: "var(--muted)", flex: 1 }}>Vídeo subido ✓</span>
                    : <Uploader accept="video/*" label="+ Subir vídeo" testid={`leccion-video-upload-${tid}-${vi}`} onDone={(res) => setVids(videos.map((x, j) => j === vi ? { ...x, file_url: res.url } : x))} />
                ) : (
                  <input value={v.url || ""} onChange={(e) => setVids(videos.map((x, j) => j === vi ? { ...x, url: e.target.value } : x))}
                    placeholder={v.tipo === "youtube" ? "https://youtube.com/watch?v=..." : "https://vimeo.com/..."}
                    style={{ ...areaStyle, flex: 1, padding: "6px 10px" }} data-testid={`leccion-video-url-${tid}-${vi}`} />
                )}
                <button type="button" className="abtn danger" onClick={() => setVids(videos.filter((_, j) => j !== vi))}>✕</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" className="abtn" data-testid={`add-video-youtube-${tid}`} onClick={() => setVids([...videos, { tipo: "youtube", url: "", file_url: "" }])}>+ Enlace YouTube</button>
              <button type="button" className="abtn" data-testid={`add-video-vimeo-${tid}`} onClick={() => setVids([...videos, { tipo: "vimeo", url: "", file_url: "" }])}>+ Enlace Vimeo</button>
              <Uploader accept="video/*" label="+ Subir vídeo" testid={`leccion-video-upload-${tid}`} onDone={(res) => setVids([...videos, { tipo: "upload", url: "", file_url: res.url }])} />
            </div>
          </div>

          {/* ---- Imágenes (varias) ---- */}
          <div>
            <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>Imágenes en línea (arrastra para ordenar)</label>
            {imagenes.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {imagenes.map((img, ii) => (
                  <div key={ii} draggable data-testid={`leccion-imagen-item-${tid}-${ii}`}
                    onDragStart={() => setDimg(ii)} onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dimg === null || dimg === ii) { setDimg(null); return; } setImgs(reorder(imagenes, dimg, ii)); setDimg(null); }}
                    style={{ position: "relative", cursor: "grab", opacity: dimg === ii ? 0.5 : 1 }}>
                    <img src={fileUrl(img)} alt="" style={{ width: 68, height: 68, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} />
                    <button type="button" className="abtn danger" style={{ position: "absolute", top: -8, right: -8, padding: "0 6px" }} onClick={() => setImgs(imagenes.filter((_, j) => j !== ii))}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <Uploader accept="image/*" label="+ Añadir imagen" testid={`leccion-imagen-upload-${tid}`}
              onDone={(res) => setImgs([...imagenes, res.url])} />
          </div>

          {/* ---- Recursos descargables (varios) ---- */}
          <div>
            <label style={{ fontSize: 12, color: "var(--muted)", display: "block", marginBottom: 6 }}>Recursos descargables · PDF, plantillas, zip (arrastra ⠿ para ordenar)</label>
            {recursos.map((r, ri) => (
              <div key={ri} draggable data-testid={`leccion-recurso-item-${tid}-${ri}`}
                onDragStart={() => setDr(ri)} onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dr === null || dr === ri) { setDr(null); return; } setRec(reorder(recursos, dr, ri)); setDr(null); }}
                style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, background: dr === ri ? "rgba(47,107,255,.08)" : "transparent", cursor: "grab" }}>
                <span style={{ color: "var(--muted)" }}>⠿</span>
                <input value={r.nombre} onChange={(e) => setRec(recursos.map((x, j) => j === ri ? { ...x, nombre: e.target.value } : x))} style={{ ...areaStyle, flex: 1, padding: "6px 10px" }} />
                <span style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase" }}>{r.tipo}</span>
                <button type="button" className="abtn danger" onClick={() => setRec(recursos.filter((_, j) => j !== ri))}>✕</button>
              </div>
            ))}
            <Uploader accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.zip" label="+ Añadir recurso" testid={`leccion-recurso-upload-${tid}`}
              onDone={(res) => setRec([...recursos, { nombre: res.filename, url: res.url, tipo: extTipo(res.filename) }])} />
          </div>
        </div>
      )}
    </div>
  );
}


function ModuleEditor({ m, mi, count, setMod, delMod, addLec, setLec, delLec, mDrag, reorderLec }) {
  const [open, setOpen] = useState(mi === 0);
  const [dragLec, setDragLec] = useState(null);
  const lecciones = m.lecciones || [];
  return (
    <div style={{ border: "1px solid var(--gold-dim, var(--line))", borderRadius: 10, marginBottom: 12, background: mDrag && mDrag.dragging ? "rgba(47,107,255,.12)" : "rgba(197,164,90,0.04)" }} data-testid={`modulo-editor-${mi}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 12px" }}>
        <span className="drag-handle" title="Arrastra para reordenar el módulo" draggable {...dragHandlers(mDrag)}
          data-testid={`modulo-drag-${mi}`} style={{ cursor: "grab", color: "var(--gold)", fontSize: 16, padding: "0 2px" }}>⠿</span>
        <span style={{ color: "var(--gold)", fontSize: 13, minWidth: 26, fontWeight: 600 }}>M{mi + 1}</span>
        <input value={m.titulo || ""} placeholder={`Título del módulo ${mi + 1}`} onChange={(e) => setMod(mi, "titulo", e.target.value)}
          style={{ ...areaStyle, flex: 1, padding: "8px 10px", fontWeight: 600 }} data-testid={`modulo-titulo-${mi}`} />
        <span style={{ fontSize: 11, color: "var(--muted)" }}>{lecciones.length} lec.</span>
        <button type="button" className="abtn" onClick={() => setOpen((o) => !o)} data-testid={`modulo-toggle-${mi}`}>{open ? "▲" : "▼ Abrir"}</button>
        <button type="button" className="abtn danger" onClick={() => delMod(mi)}>✕</button>
      </div>
      {open && (
        <div style={{ padding: "0 12px 14px" }}>
          <div className="field" style={{ margin: "0 0 12px" }}>
            <label>Descripción del módulo</label>
            <input value={m.descripcion || ""} onChange={(e) => setMod(mi, "descripcion", e.target.value)} style={areaStyle} data-testid={`modulo-descripcion-${mi}`} />
          </div>
          <label style={{ fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--muted)", display: "block", marginBottom: 8 }}>Lecciones · arrastra ⠿ para reordenar</label>
          {lecciones.map((l, li) => (
            <LessonEditor key={li} l={l} mi={mi} li={li} count={lecciones.length} setLec={setLec} delLec={delLec}
              lDrag={{
                onDragStart: () => setDragLec(li),
                onDragOver: (e) => e.preventDefault(),
                onDrop: () => { if (dragLec !== null && dragLec !== li) reorderLec(mi, dragLec, li); setDragLec(null); },
                dragging: dragLec === li,
              }} />
          ))}
          <button type="button" className="abtn" onClick={() => addLec(mi)} data-testid={`add-leccion-${mi}`}>+ Añadir lección</button>
        </div>
      )}
    </div>
  );
}

function CourseModal({ curso, onClose, onSaved }) {
  const creating = !curso;
  const normLec = (l) => {
    let videos = Array.isArray(l.videos) ? l.videos : [];
    if (videos.length === 0 && l.video_tipo) {
      videos = [{ tipo: l.video_tipo, url: l.video_url || "", file_url: l.video_file_url || "" }];
    }
    return { ...l, submodulo: l.submodulo || "", contenido: l.contenido || "", videos,
      imagenes: l.imagenes || [], recursos: l.recursos || [] };
  };
  const initCurso = curso
    ? { ...curso, modulos: (curso.modulos || []).map((m) => ({ ...m, lecciones: (m.lecciones || []).map(normLec) })) }
    : { slug: "", titulo: "", descripcion: "", categoria: "Bonus", lecciones: 0, horas: 4, orden: 10, precio_mensual: 0, acceso: "all", destacado: false, modulos: [] };
  const [f, setF] = useState(initCurso);
  const [dragMod, setDragMod] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setMod = (mi, k, v) => setF((s) => ({ ...s, modulos: s.modulos.map((m, j) => j === mi ? { ...m, [k]: v } : m) }));
  const addMod = () => setF((s) => ({ ...s, modulos: [...(s.modulos || []), { orden: (s.modulos || []).length + 1, titulo: "", descripcion: "", lecciones: [] }] }));
  const delMod = (mi) => { if (!window.confirm("¿Eliminar este módulo y sus lecciones?")) return; setF((s) => ({ ...s, modulos: s.modulos.filter((_, j) => j !== mi).map((m, j) => ({ ...m, orden: j + 1 })) })); };
  const reorderMod = (from, to) => setF((s) => {
    if (from === null || from === to) return s;
    return { ...s, modulos: reorder(s.modulos, from, to).map((m, k) => ({ ...m, orden: k + 1 })) };
  });
  const newLec = (n) => ({ orden: n, titulo: "", submodulo: "", descripcion: "", contenido: "", videos: [], video_tipo: "", video_url: "", video_file_url: "", imagenes: [], recursos: [] });
  const addLec = (mi) => setF((s) => ({ ...s, modulos: s.modulos.map((m, j) => j === mi ? { ...m, lecciones: [...(m.lecciones || []), newLec((m.lecciones || []).length + 1)] } : m) }));
  const setLec = (mi, li, k, v) => setF((s) => ({ ...s, modulos: s.modulos.map((m, j) => j === mi ? { ...m, lecciones: m.lecciones.map((l, x) => x === li ? { ...l, [k]: v } : l) } : m) }));
  const delLec = (mi, li) => setF((s) => ({ ...s, modulos: s.modulos.map((m, j) => j === mi ? { ...m, lecciones: m.lecciones.filter((_, x) => x !== li).map((l, x) => ({ ...l, orden: x + 1 })) } : m) }));
  const reorderLec = (mi, from, to) => setF((s) => ({
    ...s,
    modulos: s.modulos.map((m, j) => j !== mi ? m : { ...m, lecciones: reorder(m.lecciones, from, to).map((l, x) => ({ ...l, orden: x + 1 })) }),
  }));

  const save = async () => {
    try {
      const modulos = (f.modulos || []).map((m, j) => ({
        orden: j + 1, titulo: m.titulo || `Módulo ${j + 1}`, descripcion: m.descripcion || "",
        lecciones: (m.lecciones || []).map((l, x) => ({
          orden: x + 1, titulo: l.titulo || `Lección ${x + 1}`, submodulo: l.submodulo || "", descripcion: l.descripcion || "",
          contenido: l.contenido || "",
          videos: (l.videos || []).filter((v) => v.url || v.file_url).map((v) => ({ tipo: v.tipo || "youtube", url: v.url || "", file_url: v.file_url || "" })),
          video_tipo: "", video_url: "", video_file_url: "",
          imagenes: l.imagenes || [], recursos: l.recursos || [],
        })),
      }));
      const totalLec = modulos.reduce((a, m) => a + m.lecciones.length, 0);
      const slug = creating ? (f.slug || f.titulo).toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") : f.slug;
      const payload = { ...f, slug, modulos, lecciones: totalLec, horas: +f.horas, orden: +f.orden, precio_mensual: +f.precio_mensual, acceso: f.acceso || "all" };
      if (creating) await adminCreateCourse(payload); else await adminUpdateCourse(curso.slug, payload);
      toast.success("Curso guardado"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
  };

  return (
    <div className="amodal" onClick={onClose}>
      <div className="amodal-card" onClick={(e) => e.stopPropagation()} data-testid="course-modal" style={{ maxHeight: "90vh", overflowY: "auto", maxWidth: 720 }}>
        <h3>{creating ? "Nuevo curso" : `Editar: ${f.titulo}`}</h3>
        <Field label="Título" value={f.titulo} onChange={(e) => set("titulo", e.target.value)} data-testid="course-titulo" />
        <div className="field" style={{ marginBottom: 12 }}><label>Descripción</label>
          <textarea value={f.descripcion} onChange={(e) => set("descripcion", e.target.value)} style={{ ...areaStyle, minHeight: 70 }} /></div>
        <div className="field-row">
          <Field label="Categoría" value={f.categoria} onChange={(e) => set("categoria", e.target.value)} />
          <Field label="Horas totales" type="number" value={f.horas} onChange={(e) => set("horas", e.target.value)} />
        </div>
        <div className="field" style={{ marginBottom: 12 }}><label>¿Quién puede verlo?</label>
          <select value={f.acceso || "all"} onChange={(e) => set("acceso", e.target.value)} style={areaStyle} data-testid="course-acceso">
            <option value="all">Todos los alumnos del campus</option>
            <option value="enrolled">Solo alumnos inscritos (de pago)</option>
            <option value="vip">Solo VIP / vitalicio</option>
          </select></div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, cursor: "pointer", fontSize: 13.5, color: "var(--cream)" }}>
          <input type="checkbox" data-testid="course-destacado" checked={!!f.destacado} onChange={(e) => set("destacado", e.target.checked)} />
          Marcar como <b style={{ color: "var(--gold)" }}>NUEVO</b> (muestra sello y avisa a los alumnos al crearlo)
        </label>
        <label style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--gold)" }}>Módulos y lecciones · arrastra ⠿ para reordenar</label>
        <div style={{ margin: "10px 0 16px" }}>
          {(f.modulos || []).map((m, mi) => (
            <ModuleEditor key={mi} m={m} mi={mi} count={(f.modulos || []).length} setMod={setMod} delMod={delMod}
              addLec={addLec} setLec={setLec} delLec={delLec} reorderLec={reorderLec}
              mDrag={{
                onDragStart: () => setDragMod(mi),
                onDragOver: (e) => e.preventDefault(),
                onDrop: () => { if (dragMod !== null && dragMod !== mi) reorderMod(dragMod, mi); setDragMod(null); },
                dragging: dragMod === mi,
              }} />
          ))}
          <button className="abtn" onClick={addMod} data-testid="add-modulo">+ Añadir módulo</button>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-gold btn-sm" onClick={save} data-testid="course-save">Guardar</button>
          <button className="abtn" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function PassModal({ alumno, onClose }) {
  const [pw, setPw] = useState("");
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (pw && pw.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    setSaving(true);
    try {
      const r = await adminSetPassword(alumno.id, pw || null);
      setResult(r);
      toast.success("Contraseña actualizada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };
  const copy = () => {
    navigator.clipboard?.writeText(`Email: ${result.email}\nContraseña: ${result.password}`);
    toast.success("Credenciales copiadas");
  };
  return (
    <div className="amodal" onClick={onClose}>
      <div className="amodal-card" onClick={(e) => e.stopPropagation()} data-testid="pass-modal" style={{ maxWidth: 460 }}>
        <h3>Contraseña de {alumno.nombre || alumno.email}</h3>
        <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 14 }}>{alumno.email}</p>
        {!result ? (
          <>
            <Field label="Nueva contraseña (deja vacío para generar una automática)" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Automática si lo dejas vacío" data-testid="pass-input" />
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button className="btn btn-gold btn-sm" onClick={save} disabled={saving} data-testid="pass-save">{saving ? "Guardando…" : "Guardar contraseña"}</button>
              <button className="abtn" onClick={onClose}>Cancelar</button>
            </div>
          </>
        ) : (
          <div data-testid="pass-result">
            <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 10 }}>Comparte estas credenciales con el alumno (por WhatsApp o email personal):</p>
            <div style={{ border: "1px solid var(--line)", borderRadius: 8, padding: 14, background: "var(--ink)", fontFamily: "monospace", fontSize: 14 }}>
              <div>Email: <b style={{ color: "var(--gold)" }}>{result.email}</b></div>
              <div>Contraseña: <b style={{ color: "var(--gold)" }}>{result.password}</b></div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button className="btn btn-gold btn-sm" onClick={copy} data-testid="pass-copy">Copiar credenciales</button>
              <button className="abtn" onClick={onClose}>Cerrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CallModal({ call, onClose, onSaved }) {
  const creating = !call;
  const [f, setF] = useState(call || { dia: "", mes: "JUL", titulo: "", hora: "", tipo: "abierta", zoom_url: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try {
      const payload = { dia: f.dia, mes: f.mes, titulo: f.titulo, hora: f.hora, tipo: f.tipo, zoom_url: f.zoom_url };
      if (creating) await adminCreateCall(payload); else await adminUpdateCall(call.id, payload);
      toast.success("Llamada guardada"); onSaved(); onClose();
    } catch (e) { toast.error("Error al guardar"); }
  };
  return (
    <div className="amodal" onClick={onClose}>
      <div className="amodal-card" onClick={(e) => e.stopPropagation()} data-testid="call-modal">
        <h3>{creating ? "Nueva llamada" : "Editar llamada"}</h3>
        <div className="field-row">
          <Field label="Día" value={f.dia} onChange={(e) => set("dia", e.target.value)} />
          <Field label="Mes" value={f.mes} onChange={(e) => set("mes", e.target.value)} />
        </div>
        <Field label="Título" value={f.titulo} onChange={(e) => set("titulo", e.target.value)} data-testid="call-titulo" />
        <Field label="Hora (texto)" value={f.hora} onChange={(e) => set("hora", e.target.value)} placeholder="Martes · 19:00 (Madrid) / 12:00 (CDMX)" />
        <div className="field" style={{ marginBottom: 12 }}><label>Tipo</label>
          <select value={f.tipo} onChange={(e) => set("tipo", e.target.value)} style={areaStyle}>
            <option value="abierta">Abierta</option><option value="vip">Solo VIP</option>
          </select></div>
        <Field label="Enlace Zoom" value={f.zoom_url} onChange={(e) => set("zoom_url", e.target.value)} />
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-gold btn-sm" onClick={save} data-testid="call-save">Guardar</button>
          <button className="abtn" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function AnalyticsPanel({ data }) {
  if (!data) return <p style={{ color: "var(--muted)" }}>Cargando analítica…</p>;
  const maxV = Math.max(1, ...data.serie.map((d) => d.visitas));
  const bloque = { border: "1px solid var(--line)", borderRadius: 12, padding: 18, background: "rgba(255,255,255,0.02)" };
  return (
    <div data-testid="admin-analitica" style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <div className="astat">
        {[["Visitas totales", data.total], ["Visitas hoy", data.hoy], ["Visitantes únicos", data.unicos]].map(([k, v]) => (
          <div className="kpi" key={k}><div className="v">{v}</div><div className="k">{k}</div></div>
        ))}
      </div>

      <div style={bloque}>
        <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 16 }}>Visitas por día (últimos 30 días)</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 160 }} data-testid="analitica-grafico">
          {data.serie.map((d) => (
            <div key={d.day} title={`${fmtDay(d.day)}: ${d.visitas} visitas`} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 4 }}>
              <div style={{ width: "100%", height: `${(d.visitas / maxV) * 100}%`, minHeight: d.visitas > 0 ? 3 : 0, background: "linear-gradient(180deg, var(--gold), rgba(197,164,90,0.35))", borderRadius: "3px 3px 0 0", transition: "height .3s" }} />
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 10, color: "var(--muted)" }}>
          <span>{fmtDay(data.serie[0]?.day)}</span>
          <span>{fmtDay(data.serie[data.serie.length - 1]?.day)}</span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 18 }}>
        <div style={bloque}>
          <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 12 }}>Páginas más vistas</div>
          {data.top_pages.length === 0 && <p style={{ color: "var(--muted)", fontSize: 13 }}>Aún sin datos.</p>}
          {data.top_pages.map((p) => (
            <div key={p.path} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--line)", fontSize: 13 }}>
              <span style={{ color: "var(--cream)" }}>{p.path}</span><span style={{ color: "var(--gold)" }}>{p.visitas}</span>
            </div>
          ))}
        </div>
        <div style={bloque}>
          <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 12 }}>Dispositivos</div>
          {data.dispositivos.map((d) => (
            <div key={d.tipo} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--line)", fontSize: 13 }}>
              <span style={{ color: "var(--cream)", textTransform: "capitalize" }}>{d.tipo}</span><span style={{ color: "var(--gold)" }}>{d.visitas}</span>
            </div>
          ))}
        </div>
        <div style={bloque}>
          <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 12 }}>Países (aprox.)</div>
          {data.paises.map((c) => (
            <div key={c.pais} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--line)", fontSize: 13 }}>
              <span style={{ color: "var(--cream)" }}>{c.pais}</span><span style={{ color: "var(--gold)" }}>{c.visitas}</span>
            </div>
          ))}
        </div>
      </div>
      <p style={{ fontSize: 11, color: "var(--muted)" }}>El país es aproximado (según el idioma del navegador). Para geolocalización exacta puedes conectar Google Analytics más adelante.</p>
    </div>
  );
}

function EmpleoModal({ oferta, onClose, onSaved }) {
  const creating = !oferta;
  const [f, setF] = useState(oferta || { titulo: "", tipo: "empleo", empresa: "", ubicacion: "", descripcion: "", enlace: "", contacto: "", imagen: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    try {
      const payload = { titulo: f.titulo, tipo: f.tipo, empresa: f.empresa, ubicacion: f.ubicacion, descripcion: f.descripcion, enlace: f.enlace, contacto: f.contacto, imagen: f.imagen || "" };
      if (creating) await adminCreateEmpleo(payload); else await adminUpdateEmpleo(oferta.id, payload);
      toast.success("Oferta guardada"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
  };
  return (
    <div className="amodal" onClick={onClose}>
      <div className="amodal-card" onClick={(e) => e.stopPropagation()} data-testid="empleo-modal" style={{ maxWidth: 560 }}>
        <h3>{creating ? "Nueva oferta" : "Editar oferta"}</h3>
        <Field label="Título" value={f.titulo} onChange={(e) => set("titulo", e.target.value)} data-testid="empleo-titulo" placeholder="Ej: Especialista en automatización con n8n" />
        <div className="field-row">
          <div className="field" style={{ marginBottom: 12 }}><label>Tipo</label>
            <select value={f.tipo} onChange={(e) => set("tipo", e.target.value)} style={areaStyle} data-testid="empleo-tipo">
              <option value="empleo">Empleo</option><option value="proyecto">Proyecto</option>
            </select></div>
          <Field label="Empresa / cliente" value={f.empresa} onChange={(e) => set("empresa", e.target.value)} />
        </div>
        <Field label="Ubicación" value={f.ubicacion} onChange={(e) => set("ubicacion", e.target.value)} placeholder="Remoto · España…" />
        <div className="field" style={{ marginBottom: 12 }}><label>Descripción</label>
          <textarea value={f.descripcion} onChange={(e) => set("descripcion", e.target.value)} style={{ ...areaStyle, minHeight: 80 }} data-testid="empleo-descripcion" /></div>
        <Field label="Enlace para aplicar (URL)" value={f.enlace} onChange={(e) => set("enlace", e.target.value)} placeholder="https://…" />
        <Field label="Contacto (email o URL)" value={f.contacto} onChange={(e) => set("contacto", e.target.value)} placeholder="hola@empresa.com o WhatsApp" />
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <Uploader accept="image/*" label={f.imagen ? "Reemplazar imagen" : "+ Subir imagen/logo"} testid="empleo-imagen-upload" onDone={(res) => set("imagen", res.url)} />
          {f.imagen && <img src={fileUrl(f.imagen)} alt="" style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} />}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
          <button className="btn btn-gold btn-sm" onClick={save} data-testid="empleo-save">Guardar</button>
          <button className="abtn" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function RecursoModal({ recurso, onClose, onSaved }) {
  const creating = !recurso;
  const [f, setF] = useState(recurso
    ? { ...recurso, bloques: recurso.bloques || [] }
    : { titulo: "", categoria: "Software MAIA", descripcion: "", tipo: "paquete", url: "", file_url: "", filename: "", imagen: "", solo_vip: false, bloques: [] });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    try {
      const payload = {
        titulo: f.titulo, categoria: f.categoria || "General", descripcion: f.descripcion,
        tipo: "paquete", url: f.url || "", file_url: f.file_url || "", filename: f.filename || "",
        imagen: f.imagen || "", solo_vip: !!f.solo_vip, bloques: f.bloques || [],
      };
      if (creating) await adminCreateRecurso(payload); else await adminUpdateRecurso(recurso.id, payload);
      toast.success("Recurso guardado"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
  };
  return (
    <div className="amodal" onClick={onClose}>
      <div className="amodal-card" onClick={(e) => e.stopPropagation()} data-testid="recurso-modal" style={{ maxWidth: 620, maxHeight: "90vh", overflowY: "auto" }}>
        <h3>{creating ? "Nuevo recurso" : "Editar recurso"}</h3>
        <Field label="Título" value={f.titulo} onChange={(e) => set("titulo", e.target.value)} data-testid="recurso-titulo" placeholder="Ej: Agente de atención al cliente (n8n)" />
        <Field label="Sección (agrupa recursos: Software MAIA, Prompts, Plantillas, Agentes…)" value={f.categoria} onChange={(e) => set("categoria", e.target.value)} placeholder="Software MAIA · Prompts · Plantillas · Agentes" />
        <div className="field" style={{ marginBottom: 12 }}><label>Descripción</label>
          <textarea value={f.descripcion} onChange={(e) => set("descripcion", e.target.value)} style={{ ...areaStyle, minHeight: 70 }} data-testid="recurso-descripcion" /></div>

        <div className="field" style={{ marginBottom: 12 }}>
          <label>Portada (opcional)</label>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Uploader accept="image/*" label={f.imagen ? "Cambiar portada" : "+ Subir portada"} testid="recurso-portada-upload" onDone={(res) => set("imagen", res.url)} />
            {f.imagen && <img src={fileUrl(f.imagen)} alt="" style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} />}
          </div>
        </div>

        <div className="field" style={{ marginBottom: 12 }}>
          <label>Contenido · varios documentos, vídeos, enlaces web, imágenes…</label>
          <Bloques value={f.bloques} onChange={(list) => set("bloques", list)} tid="recurso" />
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0 10px", cursor: "pointer", fontSize: 13.5, color: "var(--cream)" }}>
          <input type="checkbox" data-testid="recurso-solo-vip" checked={!!f.solo_vip} onChange={(e) => set("solo_vip", e.target.checked)} />
          Recurso <b style={{ color: "var(--gold)" }}>solo para VIP / vitalicio</b>
        </label>
        <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
          <button className="btn btn-gold btn-sm" onClick={save} data-testid="recurso-save">Guardar</button>
          <button className="abtn" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function AvisosPanel() {
  const [f, setF] = useState({ titulo: "", cuerpo: "", url: "", target: "all", user_id: "", destacado: false });
  const [data, setData] = useState({ notificaciones: [], suscriptores_push: 0 });
  const [alumnos, setAlumnos] = useState([]);
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const load = () => adminGetNotifications().then(setData).catch(() => {});
  useEffect(() => { load(); adminUsers().then((u) => setAlumnos(Array.isArray(u) ? u : (u?.alumnos || u?.usuarios || []))).catch(() => {}); }, []);
  const send = async () => {
    if (!f.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    if (f.target === "user" && !f.user_id) { toast.error("Elige el alumno destinatario"); return; }
    setSending(true);
    try {
      const r = await adminSendNotification({ titulo: f.titulo, cuerpo: f.cuerpo, url: f.url, target: f.target, user_id: f.user_id, destacado: f.destacado });
      toast.success(`Aviso enviado · push a ${r.push_enviadas ?? 0} dispositivo(s)`);
      setF({ titulo: "", cuerpo: "", url: "", target: "all", user_id: "", destacado: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al enviar"); }
    finally { setSending(false); }
  };
  const del = async (id) => { if (!window.confirm("¿Eliminar este aviso?")) return; await adminDeleteNotification(id); load(); };
  return (
    <div data-testid="admin-avisos" style={{ display: "grid", gridTemplateColumns: "minmax(280px,440px) 1fr", gap: 26 }}>
      <div style={{ border: "1px solid var(--line)", borderRadius: 12, padding: 22, background: "rgba(255,255,255,0.02)", alignSelf: "start" }}>
        <h3 className="serif" style={{ fontSize: 18, marginBottom: 4 }}>Enviar aviso</h3>
        <p style={{ color: "var(--muted)", fontSize: 12.5, marginBottom: 16 }}>Aparece en la campanita del campus y, si tienen los avisos activados, les llega como push ({data.suscriptores_push} suscritos).</p>
        <div className="field" style={{ marginBottom: 12 }}><label>Destinatario</label>
          <select value={f.target} onChange={(e) => set("target", e.target.value)} style={areaStyle} data-testid="aviso-target">
            <option value="all">Todos los alumnos</option>
            <option value="vip">Solo VIP / vitalicio</option>
            <option value="user">Un alumno concreto</option>
          </select></div>
        {f.target === "user" && (
          <div className="field" style={{ marginBottom: 12 }}><label>Alumno</label>
            <select value={f.user_id} onChange={(e) => set("user_id", e.target.value)} style={areaStyle} data-testid="aviso-alumno">
              <option value="">— Elige alumno —</option>
              {alumnos.map((a) => <option key={a.id} value={a.id}>{a.nombre || a.name} · {a.email}</option>)}
            </select></div>
        )}
        <Field label="Título" value={f.titulo} onChange={(e) => set("titulo", e.target.value)} data-testid="aviso-titulo" placeholder="Ej: Nueva mentoría el martes" />
        <div className="field" style={{ marginBottom: 12 }}><label>Mensaje</label>
          <textarea value={f.cuerpo} onChange={(e) => set("cuerpo", e.target.value)} style={{ ...areaStyle, minHeight: 90 }} data-testid="aviso-cuerpo" placeholder="Detalles del aviso…" /></div>
        <Field label="Enlace al pulsar (opcional)" value={f.url} onChange={(e) => set("url", e.target.value)} placeholder="/campus o https://…" />
        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0 12px", cursor: "pointer", fontSize: 13.5, color: "var(--cream)" }}>
          <input type="checkbox" data-testid="aviso-destacado" checked={f.destacado} onChange={(e) => set("destacado", e.target.checked)} />
          Mostrar como <b style={{ color: "var(--gold)" }}>pop-up grande</b> a pantalla completa
        </label>
        <button className="btn btn-gold btn-sm" onClick={send} disabled={sending} data-testid="aviso-enviar" style={{ marginTop: 6 }}>{sending ? "Enviando…" : "Enviar aviso →"}</button>
      </div>
      <div>
        <h3 className="serif" style={{ fontSize: 18, marginBottom: 12 }}>Avisos enviados</h3>
        <table className="atable">
          <thead><tr><th>Fecha</th><th>Título</th><th>Mensaje</th><th></th></tr></thead>
          <tbody>{data.notificaciones.map((n) => (
            <tr key={n.id}><td style={{ whiteSpace: "nowrap" }}>{fmt(n.created_at)}</td><td>{n.titulo}{n.destacado ? " ⭐" : ""}</td><td>{n.cuerpo}</td>
              <td style={{ width: 60 }}><button className="abtn danger" onClick={() => del(n.id)}>Borrar</button></td></tr>))}
            {data.notificaciones.length === 0 && <tr><td colSpan={4} style={{ color: "var(--muted)" }}>Aún no has enviado avisos.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditAlumnoModal({ alumno, onClose, onSaved }) {
  const [nombre, setNombre] = useState(alumno.nombre || "");
  const [email, setEmail] = useState(alumno.email || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!email.includes("@")) { toast.error("Email no válido"); return; }
    setSaving(true);
    try {
      await adminEditUser(alumno.id, { nombre, email });
      toast.success("Alumno actualizado ✓");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="amodal" onClick={onClose}>
      <div className="amodal-card" onClick={(e) => e.stopPropagation()} data-testid="editar-alumno-modal" style={{ maxWidth: 440 }}>
        <h3>Editar alumno</h3>
        <div className="field" style={{ marginBottom: 12 }}><label>Nombre</label>
          <input value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="editar-alumno-nombre" style={{ ...areaStyle }} /></div>
        <div className="field" style={{ marginBottom: 16 }}><label>Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="editar-alumno-email" style={{ ...areaStyle }} type="email" /></div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-gold btn-sm" onClick={save} disabled={saving} data-testid="editar-alumno-guardar">{saving ? "Guardando…" : "Guardar"}</button>
          <button className="abtn" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function NuevoAlumnoModal({ cursos, onClose, onSaved }) {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [vip, setVip] = useState(false);
  const [enrolled, setEnrolled] = useState([]);
  const [saving, setSaving] = useState(false);
  const [creado, setCreado] = useState(null);
  const toggle = (slug) => setEnrolled((s) => s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);
  const save = async () => {
    if (!email.includes("@")) { toast.error("Introduce un email válido"); return; }
    setSaving(true);
    try {
      const r = await adminCreateUser({ nombre, email, password: password || null, vip_vitalicio: vip, enrolled_courses: enrolled });
      setCreado(r);
      toast.success(`Alumno ${email} creado`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al crear el alumno"); }
    finally { setSaving(false); }
  };
  return (
    <div className="amodal" onClick={onClose}>
      <div className="amodal-card" onClick={(e) => e.stopPropagation()} data-testid="nuevo-alumno-modal" style={{ maxWidth: 520 }}>
        <h3>Crear alumno manualmente</h3>
        {creado ? (
          <div data-testid="alumno-creado">
            <p style={{ color: "var(--cream)", fontSize: 14, marginBottom: 10 }}>Cuenta creada para <b>{creado.email}</b>.</p>
            <div style={{ background: "rgba(197,164,90,.1)", border: "1px solid var(--gold)", borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--muted)" }}>Contraseña {creado.generated ? "(generada)" : ""}</div>
              <div style={{ fontSize: 18, color: "var(--gold)", fontWeight: 700, fontFamily: "monospace" }} data-testid="alumno-password">{creado.password}</div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>Guárdala y compártela con el alumno. No se volverá a mostrar.</div>
            </div>
            <button className="btn btn-gold btn-sm" onClick={onClose} data-testid="alumno-creado-cerrar">Hecho</button>
            <button className="abtn" style={{ marginLeft: 8 }} data-testid="alumno-copiar" onClick={async () => {
              const txt = `Acceso a Liberti Club IA\nEmail/usuario: ${creado.email}\nContraseña: ${creado.password}\nEntra en: https://liberticlubia.com/login`;
              try { await navigator.clipboard.writeText(txt); toast.success("Credenciales copiadas ✓"); }
              catch { toast.error("No se pudo copiar. Cópialas manualmente."); }
            }}>Copiar credenciales</button>
          </div>
        ) : (
          <>
            <div className="field" style={{ marginBottom: 12 }}><label>Nombre</label>
              <input value={nombre} onChange={(e) => setNombre(e.target.value)} data-testid="nuevo-alumno-nombre" style={{ ...areaStyle }} placeholder="Nombre y apellido" /></div>
            <div className="field" style={{ marginBottom: 12 }}><label>Email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="nuevo-alumno-email" style={{ ...areaStyle }} placeholder="alumno@correo.com" type="email" /></div>
            <div className="field" style={{ marginBottom: 12 }}><label>Contraseña (vacío = generar automática)</label>
              <input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="nuevo-alumno-password" style={{ ...areaStyle }} placeholder="Mínimo 6 caracteres o déjalo vacío" /></div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", cursor: "pointer", marginBottom: 10 }}>
              <input type="checkbox" data-testid="nuevo-alumno-vip" checked={vip} onChange={(e) => setVip(e.target.checked)} />
              <span><b style={{ color: "var(--gold)" }}>VIP / vitalicio</b> — acceso total al campus</span>
            </label>
            {cursos.length > 0 && (
              <>
                <p style={{ color: "var(--muted)", fontSize: 12.5, margin: "6px 0 8px" }}>Cursos con acceso (opcional):</p>
                <div style={{ display: "grid", gap: 8, maxHeight: 180, overflow: "auto", marginBottom: 14 }}>
                  {cursos.map((c) => (
                    <label key={c.slug} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 8, cursor: "pointer" }}>
                      <input type="checkbox" data-testid={`nuevo-alumno-curso-${c.slug}`} checked={enrolled.includes(c.slug)} onChange={() => toggle(c.slug)} />
                      <span style={{ fontSize: 13.5 }}>{c.titulo}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn btn-gold btn-sm" onClick={save} disabled={saving} data-testid="nuevo-alumno-guardar">{saving ? "Creando…" : "Crear alumno"}</button>
              <button className="abtn" onClick={onClose}>Cancelar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AccessModal({ alumno, cursos, onClose, onSaved }) {
  const [vip, setVip] = useState(!!alumno.vip);
  const [enrolled, setEnrolled] = useState(alumno.enrolled_courses || []);
  const [saving, setSaving] = useState(false);
  const toggle = (slug) => setEnrolled((s) => s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);
  const save = async () => {
    setSaving(true);
    try {
      await adminSetAccess(alumno.id, { enrolled_courses: enrolled, vip_vitalicio: vip });
      toast.success(`Acceso actualizado para ${alumno.nombre || alumno.email}`);
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="amodal" onClick={onClose}>
      <div className="amodal-card" onClick={(e) => e.stopPropagation()} data-testid="acceso-modal" style={{ maxWidth: 520 }}>
        <h3>Acceso de {alumno.nombre || alumno.email}</h3>
        <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 0", borderBottom: "1px solid var(--line)", cursor: "pointer" }}>
          <input type="checkbox" data-testid="acceso-vip" checked={vip} onChange={(e) => setVip(e.target.checked)} />
          <span><b style={{ color: "var(--gold)" }}>VIP / vitalicio</b> — acceso total a todo el campus</span>
        </label>
        <p style={{ color: "var(--muted)", fontSize: 12.5, margin: "14px 0 8px" }}>Cursos inscritos manualmente:</p>
        <div style={{ display: "grid", gap: 8, maxHeight: 260, overflow: "auto" }}>
          {cursos.map((c) => (
            <label key={c.slug} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid var(--line)", borderRadius: 8, cursor: "pointer" }}>
              <input type="checkbox" data-testid={`acceso-curso-${c.slug}`} checked={enrolled.includes(c.slug)} onChange={() => toggle(c.slug)} />
              <span style={{ fontSize: 13.5 }}>{c.titulo} <span style={{ color: "var(--muted)" }}>· {c.acceso === "vip" ? "VIP" : (c.acceso || "enrolled") === "all" ? "Todos" : "Solo inscritos"}</span></span>
            </label>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button className="btn btn-gold btn-sm" onClick={save} disabled={saving} data-testid="acceso-guardar">{saving ? "Guardando…" : "Guardar acceso"}</button>
          <button className="abtn" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function SolicitudesPanel() {
  const [data, setData] = useState({ solicitudes: [], pendientes: 0 });
  const [responder, setResponder] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const load = () => adminSolicitudes().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (id) => { await adminToggleSolicitud(id); load(); };
  const del = async (id) => { if (!window.confirm("¿Eliminar esta solicitud?")) return; await adminDeleteSolicitud(id); load(); };
  const enviarResp = async () => {
    if (!msg.trim()) { toast.error("Escribe una respuesta"); return; }
    setSending(true);
    try {
      await adminResponderSolicitud(responder.id, msg);
      toast.success(`Respuesta enviada a ${responder.nombre || "el alumno"}`);
      setResponder(null); setMsg(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al enviar"); }
    finally { setSending(false); }
  };
  return (
    <div data-testid="admin-solicitudes">
      <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 14 }}>
        Solicitudes de alumnos listos para dar el paso. <b style={{ color: "var(--gold)" }}>{data.pendientes}</b> pendiente(s).
      </p>
      <table className="atable">
        <thead><tr><th>Fecha</th><th>Alumno</th><th>Email</th><th>Solicitud</th><th>Mensaje</th><th>Respuesta</th><th>Estado</th><th></th></tr></thead>
        <tbody>{data.solicitudes.map((s) => (
          <tr key={s.id}>
            <td style={{ whiteSpace: "nowrap" }}>{fmt(s.created_at)}</td>
            <td>{s.nombre}</td>
            <td>{s.email}</td>
            <td>{SOLICITUD_LABELS[s.tipo] || "Solicitud"}</td>
            <td>{s.mensaje || "—"}</td>
            <td style={{ color: s.respuesta ? "var(--ok)" : "var(--muted)", maxWidth: 200 }}>{s.respuesta || "—"}</td>
            <td><button className="abtn" data-testid={`solicitud-estado-${s.id}`} onClick={() => toggle(s.id)}>{s.estado === "atendida" ? "✓ Atendida" : "Pendiente"}</button></td>
            <td style={{ whiteSpace: "nowrap" }}>
              <button className="abtn" data-testid={`solicitud-responder-${s.id}`} onClick={() => { setResponder(s); setMsg(s.respuesta || ""); }}>Responder</button>{" "}
              <button className="abtn danger" onClick={() => del(s.id)}>Borrar</button>
            </td>
          </tr>))}
          {data.solicitudes.length === 0 && <tr><td colSpan={8} style={{ color: "var(--muted)" }}>Sin solicitudes todavía.</td></tr>}
        </tbody>
      </table>

      {responder && (
        <div className="amodal" onClick={() => setResponder(null)}>
          <div className="amodal-card" onClick={(e) => e.stopPropagation()} data-testid="responder-modal" style={{ maxWidth: 520 }}>
            <h3>Responder a {responder.nombre || "alumno"}</h3>
            <p style={{ color: "var(--muted)", fontSize: 12.5, marginBottom: 14 }}>
              Solicitud: <b style={{ color: "var(--cream)" }}>{SOLICITUD_LABELS[responder.tipo] || "Solicitud"}</b>. Tu mensaje le llegará a su campanita del campus{responder.mensaje ? ` — dijo: “${responder.mensaje}”` : ""}.
            </p>
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} style={{ ...areaStyle, minHeight: 110 }} data-testid="responder-mensaje"
              placeholder="Ej: ¡Genial! Te hemos asignado un mentor. Entra el martes a la llamada de las 19:00 y prepara tu propuesta…" />
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <button className="btn btn-gold btn-sm" onClick={enviarResp} disabled={sending} data-testid="responder-enviar">{sending ? "Enviando…" : "Enviar respuesta →"}</button>
              <button className="abtn" onClick={() => setResponder(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("Resumen");
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [cursos, setCursos] = useState([]);
  const [offer, setOffer] = useState(null);
  const [calls, setCalls] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [chat, setChat] = useState([]);
  const [posts, setPosts] = useState([]);
  const [empleos, setEmpleos] = useState([]);
  const [recursos, setRecursos] = useState([]);
  const [editCourse, setEditCourse] = useState(undefined);
  const [editCall, setEditCall] = useState(undefined);
  const [editPass, setEditPass] = useState(null);
  const [editEmpleo, setEditEmpleo] = useState(undefined);
  const [editRecurso, setEditRecurso] = useState(undefined);
  const [editAcceso, setEditAcceso] = useState(null);
  const [nuevoAlumno, setNuevoAlumno] = useState(false);
  const [devReqs, setDevReqs] = useState([]);
  const [editAlumno, setEditAlumno] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragRec, setDragRec] = useState(null);
  const [dragSec, setDragSec] = useState(null);

  const loadAll = () => {
    adminStats().then(setStats).catch(() => {});
    adminAnalytics().then(setAnalytics).catch(() => {});
    getCourses().then(setCursos).catch(() => {});
    getOffer().then(setOffer).catch(() => {});
    adminGetCalls().then(setCalls).catch(() => {});
    adminBookings().then(setBookings).catch(() => {});
    adminLeads().then(setLeads).catch(() => {});
    adminUsers().then(setUsers).catch(() => {});
    adminDeviceRequests().then(setDevReqs).catch(() => {});
    adminChat().then(setChat).catch(() => {});
    getPosts().then(setPosts).catch(() => {});
    adminEmpleos().then(setEmpleos).catch(() => {});
    adminRecursos().then(setRecursos).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (user && user.role !== "admin") navigate("/campus"); }, [user, navigate]);

  if (user && user.role !== "admin") return null;

  const saveOffer = async () => {
    try {
      await adminUpdateOffer({
        plazas_total: +offer.plazas_total, plazas_vendidas: +offer.plazas_vendidas,
        precio_oferta: +offer.precio_oferta, precio_despues: +offer.precio_despues,
        fecha_fin_countdown: offer.fecha_fin_countdown,
      });
      toast.success("Oferta actualizada"); loadAll();
    } catch (e) { toast.error("Error al guardar la oferta"); }
  };
  const delCall = async (id) => { if (!window.confirm("¿Eliminar llamada?")) return; await adminDeleteCall(id); toast.success("Llamada eliminada"); loadAll(); };
  const toggleLead = async (id) => { await adminToggleLead(id); loadAll(); };
  const delChat = async (id) => { await adminDeleteChat(id); loadAll(); };
  const delPost = async (id) => { await adminDeletePost(id); loadAll(); };
  const delEmpleo = async (id) => { if (!window.confirm("¿Eliminar esta oferta?")) return; await adminDeleteEmpleo(id); toast.success("Oferta eliminada"); loadAll(); };
  const delRecurso = async (id) => { if (!window.confirm("¿Eliminar este recurso?")) return; await adminDeleteRecurso(id); toast.success("Recurso eliminado"); loadAll(); };

  const onDropCourse = async (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); return; }
    const arr = [...cursos];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(toIdx, 0, moved);
    setCursos(arr);
    setDragIdx(null);
    try { await adminReorderCourses(arr.map((c) => c.slug)); toast.success("Orden guardado"); }
    catch (e) { toast.error("No se pudo guardar el orden"); loadAll(); }
  };

  const persistRecursos = async (arr) => {
    setRecursos(arr);
    try { await adminReorderRecursos(arr.map((r) => r.id)); toast.success("Orden guardado"); }
    catch (e) { toast.error("No se pudo guardar el orden"); loadAll(); }
  };

  const onDropRecurso = async (toIdx) => {
    if (dragRec === null || dragRec === toIdx) { setDragRec(null); return; }
    const arr = [...recursos];
    const [moved] = arr.splice(dragRec, 1);
    arr.splice(toIdx, 0, moved);
    setDragRec(null);
    await persistRecursos(arr);
  };

  // agrupa recursos por sección (categoría) manteniendo el orden actual
  const recGroups = (() => {
    const order = [], map = {};
    recursos.forEach((r) => {
      const sec = r.categoria || "General";
      if (!map[sec]) { map[sec] = []; order.push(sec); }
      map[sec].push(r);
    });
    return order.map((sec) => ({ sec, items: map[sec] }));
  })();

  const onDropSeccion = async (toIdx) => {
    if (dragSec === null || dragSec === toIdx) { setDragSec(null); return; }
    const groups = [...recGroups];
    const [moved] = groups.splice(dragSec, 1);
    groups.splice(toIdx, 0, moved);
    setDragSec(null);
    await persistRecursos(groups.flatMap((g) => g.items));
  };

  return (
    <div className="lci"><div className="admin-wrap">
      <div className="admin-head">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="logo" onClick={() => navigate("/campus")} style={{ cursor: "pointer" }}>
            <div className="logo-mark">L</div><div className="logo-txt" style={{ fontSize: 11 }}>ADMIN <em>IA</em></div>
          </div>
          <h1>Panel de administración</h1>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <NotificationBell fixed={false} />
          <button className="abtn" onClick={() => navigate("/campus")}>← Campus</button>
          <button className="abtn danger" onClick={() => { logout(); navigate("/"); }}>Salir</button>
        </div>
      </div>

      <div className="comu-tabs">
        {TABS.map((t) => <button key={t} data-testid={`admin-tab-${t}`} className={`comu-tab ${tab === t ? "on" : ""}`} onClick={() => setTab(t)}>{t}</button>)}
      </div>

      {tab === "Resumen" && stats && (
        <div className="astat" data-testid="admin-resumen">
          {[["Usuarios", stats.usuarios], ["VIPs", stats.vips], ["Cursos", stats.cursos], ["Ventas pagadas", stats.ventas],
            ["Visitas web", stats.visitas ?? 0], ["Visitas hoy", stats.visitas_hoy ?? 0],
            ["Plazas vendidas", `${stats.plazas_vendidas}/${stats.plazas_total}`], ["Leads", stats.leads],
            ["Leads pendientes", stats.leads_pendientes], ["Citas", stats.citas]].map(([k, v]) => (
            <div className="kpi" key={k}><div className="v">{v}</div><div className="k">{k}</div></div>
          ))}
        </div>
      )}

      {tab === "Analítica" && <AnalyticsPanel data={analytics} />}

      {tab === "Cursos" && (
        <div data-testid="admin-cursos" style={{ maxWidth: 760 }}>
          <button className="btn btn-gold btn-sm" style={{ marginBottom: 16 }} data-testid="nuevo-curso" onClick={() => setEditCourse(null)}>+ Nuevo curso (bonus)</button>
          <p style={{ color: "var(--muted)", fontSize: 12, marginBottom: 12 }}>Arrastra las tarjetas para cambiar el orden en que los alumnos ven los cursos.</p>
          <div style={{ display: "grid", gap: 12 }}>
            {cursos.map((c, i) => (
              <div key={c.slug} data-testid={`curso-${c.slug}`} draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropCourse(i)}
                style={{ border: "1px solid var(--line)", borderRadius: 12, padding: 20, background: dragIdx === i ? "rgba(47,107,255,.08)" : "rgba(255,255,255,0.02)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, cursor: "grab" }}>
                <div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ color: "var(--muted)", fontSize: 16 }}>⠿</span>
                    <span className="chip">{c.categoria}</span>
                    {c.destacado && <span className="chip-nuevo">NUEVO</span>}
                    {c.acceso === "vip" && <span className="chip-vip">VIP</span>}
                  </div>
                  <h3 className="serif" style={{ fontSize: 19, fontWeight: 600, margin: "8px 0 4px" }}>{c.titulo}</h3>
                  <p style={{ color: "var(--muted)", fontSize: 12.5 }}>{(c.modulos || []).length} módulos · {c.lecciones || 0} lecciones · {c.acceso === "vip" ? "Solo VIP" : (c.acceso || "enrolled") === "all" ? "Todos" : "Solo inscritos"}</p>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button className="abtn" data-testid={`editar-curso-${c.slug}`} onClick={() => setEditCourse(c)}>Editar →</button>
                  {c.slug !== "master" && <button className="abtn danger" onClick={async () => { if (window.confirm("¿Eliminar este curso?")) { await adminDeleteCourse(c.slug); loadAll(); } }}>Borrar</button>}
                </div>
              </div>
            ))}
            {cursos.length === 0 && <p style={{ color: "var(--muted)" }}>Cargando cursos…</p>}
          </div>
        </div>
      )}

      {tab === "Oferta" && offer && (
        <div style={{ maxWidth: 460 }} data-testid="admin-oferta">
          <Field label="Plazas totales" type="number" value={offer.plazas_total} onChange={(e) => setOffer({ ...offer, plazas_total: e.target.value })} />
          <Field label="Plazas vendidas" type="number" value={offer.plazas_vendidas} onChange={(e) => setOffer({ ...offer, plazas_vendidas: e.target.value })} data-testid="oferta-vendidas" />
          <Field label="Precio oferta (€)" type="number" value={offer.precio_oferta} onChange={(e) => setOffer({ ...offer, precio_oferta: e.target.value })} />
          <Field label="Precio después (€)" type="number" value={offer.precio_despues} onChange={(e) => setOffer({ ...offer, precio_despues: e.target.value })} />
          <Field label="Fin countdown (ISO)" value={offer.fecha_fin_countdown} onChange={(e) => setOffer({ ...offer, fecha_fin_countdown: e.target.value })} />
          <button className="btn btn-gold btn-sm" onClick={saveOffer} data-testid="oferta-guardar">Guardar oferta</button>
        </div>
      )}

      {tab === "Llamadas" && (
        <>
          <button className="btn btn-gold btn-sm" style={{ marginBottom: 16 }} data-testid="nueva-llamada" onClick={() => setEditCall(null)}>+ Nueva llamada</button>
          <table className="atable" data-testid="admin-llamadas">
            <thead><tr><th>Fecha</th><th>Título</th><th>Tipo</th><th>Reservas</th><th></th></tr></thead>
            <tbody>{calls.map((c) => (
              <tr key={c.id}><td>{c.dia} {c.mes}</td><td>{c.titulo}<div style={{ fontSize: 11, color: "var(--muted)" }}>{c.hora}</div></td>
                <td>{c.tipo === "vip" ? "Solo VIP" : "Abierta"}</td>
                <td>{c.reservas_count}{c.reservas_count > 0 && <div style={{ fontSize: 11, color: "var(--muted)" }}>{c.reservas.map((r) => r.email).join(", ")}</div>}</td>
                <td style={{ whiteSpace: "nowrap" }}><button className="abtn" onClick={() => setEditCall(c)}>Editar</button>{" "}<button className="abtn danger" onClick={() => delCall(c.id)}>Borrar</button></td>
              </tr>))}
            </tbody>
          </table>
        </>
      )}

      {tab === "Citas" && (
        <table className="atable" data-testid="admin-citas">
          <thead><tr><th>Día</th><th>Hora</th><th>Nombre</th><th>Email</th><th>Teléfono</th><th>Estado</th></tr></thead>
          <tbody>{bookings.map((b) => (
            <tr key={b.id}><td>{b.dia_label || b.dia}</td><td>{b.hora}</td><td>{b.nombre}</td><td>{b.email}</td><td>{b.telefono}</td><td>{b.estado}</td></tr>))}
            {bookings.length === 0 && <tr><td colSpan={6} style={{ color: "var(--muted)" }}>Sin citas todavía.</td></tr>}
          </tbody>
        </table>
      )}

      {tab === "Leads" && (
        <table className="atable" data-testid="admin-leads">
          <thead><tr><th>Fecha</th><th>Nombre</th><th>Email</th><th>Teléfono</th><th>Mensaje</th><th>Estado</th></tr></thead>
          <tbody>{leads.map((l) => (
            <tr key={l.id}><td>{fmt(l.fecha)}</td><td>{l.nombre}</td><td>{l.email}</td><td>{l.telefono}</td><td>{l.mensaje}</td>
              <td><button className="abtn" onClick={() => toggleLead(l.id)}>{l.atendido ? "✓ Atendido" : "Pendiente"}</button></td></tr>))}
            {leads.length === 0 && <tr><td colSpan={6} style={{ color: "var(--muted)" }}>Sin leads todavía.</td></tr>}
          </tbody>
        </table>
      )}

      {tab === "Alumnos" && (
        <>
          <button className="btn btn-gold btn-sm" style={{ marginBottom: 16 }} data-testid="nuevo-alumno" onClick={() => setNuevoAlumno(true)}>+ Nuevo alumno</button>
          {devReqs.length > 0 && (
            <div data-testid="device-requests" style={{ marginBottom: 16, padding: "14px 16px", borderRadius: 14, border: "1px solid var(--gold)", background: "rgba(197,164,90,.08)" }}>
              <div style={{ fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--gold)", fontWeight: 700, marginBottom: 10 }}>🔓 Solicitudes de cambio de dispositivo ({devReqs.length})</div>
              {devReqs.map((r) => (
                <div key={r.id} data-testid={`devreq-${r.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                  <span style={{ flex: 1, fontSize: 13.5 }}><b>{r.nombre || "—"}</b> · {r.email}</span>
                  <button className="btn btn-gold btn-sm" data-testid={`devreq-aprobar-${r.id}`} onClick={async () => { try { await adminApproveDeviceRequest(r.id); toast.success("Aprobado: el alumno ya puede entrar desde el nuevo dispositivo"); loadAll(); } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aprobar"); } }}>Aprobar</button>
                </div>
              ))}
            </div>
          )}
          <table className="atable" data-testid="admin-alumnos">
          <thead><tr><th>Nombre</th><th>Email</th><th>Rol</th><th>Cursos</th><th>Activos</th><th>Progreso</th><th>Racha</th><th>Alta</th><th>Acceso</th></tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id}><td>{u.nombre}</td><td>{u.email}</td><td>{u.role}{u.vip ? " ⭐" : ""}</td><td>{u.cursos_acceso}</td><td>{u.cursos_activos}</td><td>{u.progreso_medio}%</td><td>{u.racha}</td><td>{u.alta ? fmt(u.alta) : "—"}</td>
              <td>{u.role !== "admin" && <><button className="abtn" data-testid={`editar-alumno-${u.id}`} onClick={() => setEditAlumno(u)}>Editar</button>{" "}<button className="abtn" data-testid={`acceso-${u.id}`} onClick={() => setEditAcceso(u)}>Acceso</button>{" "}<button className="abtn" data-testid={`reset-pass-${u.id}`} onClick={() => setEditPass(u)}>Contraseña</button>{" "}<button className="abtn" data-testid={`reset-devices-${u.id}`} title={`Dispositivos vinculados: ${u.dispositivos ?? 0}`} onClick={async () => { if (window.confirm(`¿Liberar los dispositivos de ${u.nombre || u.email}? Podrá volver a entrar desde un ordenador y un móvil nuevos.`)) { try { await adminResetDevices(u.id); toast.success("Dispositivos liberados"); loadAll(); } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo"); } } }}>Dispositivos ({u.dispositivos ?? 0})</button>{" "}<button className="abtn danger" data-testid={`borrar-alumno-${u.id}`} onClick={async () => { if (window.confirm(`¿Eliminar la cuenta de ${u.nombre || u.email}? No se puede deshacer.`)) { try { await adminDeleteUser(u.id); toast.success("Alumno eliminado"); loadAll(); } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); } } }}>Borrar</button></>}</td></tr>))}
          </tbody>
        </table>
        </>
      )}

      {tab === "Empleo" && (
        <>
          <button className="btn btn-gold btn-sm" style={{ marginBottom: 16 }} data-testid="nueva-oferta" onClick={() => setEditEmpleo(null)}>+ Nueva oferta</button>
          <table className="atable" data-testid="admin-empleo">
            <thead><tr><th>Título</th><th>Tipo</th><th>Empresa</th><th>Ubicación</th><th></th></tr></thead>
            <tbody>{empleos.map((o) => (
              <tr key={o.id}><td>{o.titulo}</td><td>{o.tipo === "proyecto" ? "Proyecto" : "Empleo"}</td><td>{o.empresa || "—"}</td><td>{o.ubicacion || "—"}</td>
                <td style={{ whiteSpace: "nowrap" }}><button className="abtn" onClick={() => setEditEmpleo(o)}>Editar</button>{" "}<button className="abtn danger" onClick={() => delEmpleo(o.id)}>Borrar</button></td></tr>))}
              {empleos.length === 0 && <tr><td colSpan={5} style={{ color: "var(--muted)" }}>Sin ofertas todavía. Publica la primera.</td></tr>}
            </tbody>
          </table>
        </>
      )}

      {tab === "Recursos" && (
        <>
          <button className="btn btn-gold btn-sm" style={{ marginBottom: 8 }} data-testid="nuevo-recurso" onClick={() => setEditRecurso(null)}>+ Nuevo recurso</button>
          <p style={{ color: "var(--muted)", fontSize: 12, marginBottom: 12 }}>Arrastra la cabecera <b>⠿ Sección</b> para mover un bloque entero, o una fila para ordenar recursos dentro de su sección (el orden se refleja en el campus).</p>
          {recursos.length === 0 ? (
            <p style={{ color: "var(--muted)" }} data-testid="admin-recursos-vacio">Sin recursos todavía. Sube el primero.</p>
          ) : (
            <div data-testid="admin-recursos">
              {recGroups.map((g, si) => (
                <div key={g.sec} data-testid={`admin-seccion-${g.sec}`} style={{ marginBottom: 18 }}>
                  <div draggable data-testid={`seccion-drag-${g.sec}`}
                    onDragStart={() => setDragSec(si)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropSeccion(si)}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, cursor: "grab",
                      background: dragSec === si ? "rgba(47,107,255,.14)" : "rgba(255,255,255,.04)", border: "1px solid var(--line)", marginBottom: 6 }}>
                    <span style={{ color: "var(--muted)" }}>⠿</span>
                    <b style={{ color: "var(--gold)" }}>{g.sec}</b>
                    <span style={{ color: "var(--muted)", fontSize: 12 }}>· {g.items.length}</span>
                  </div>
                  <table className="atable">
                    <tbody>{g.items.map((r) => {
                      const gi = recursos.indexOf(r);
                      return (
                        <tr key={r.id} data-testid={`admin-recurso-${r.id}`} draggable
                          onDragStart={(e) => { e.stopPropagation(); setDragRec(gi); }}
                          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.stopPropagation(); onDropRecurso(gi); }}
                          style={{ background: dragRec === gi ? "rgba(47,107,255,.08)" : "transparent", cursor: "grab" }}>
                          <td style={{ color: "var(--muted)", width: 24 }}>⠿</td>
                          <td>{r.titulo}{r.solo_vip ? " ⭐" : ""}</td>
                          <td>{r.tipo === "paquete" ? `Contenido${(r.bloques || []).length ? ` · ${r.bloques.length}` : ""}` : r.tipo === "archivo" ? "Archivo" : r.tipo === "youtube" ? "YouTube" : r.tipo === "imagen" ? "Imagen" : "Enlace"}</td>
                          <td style={{ whiteSpace: "nowrap" }}><button className="abtn" data-testid={`editar-recurso-${r.id}`} onClick={() => setEditRecurso(r)}>Editar</button>{" "}<button className="abtn danger" data-testid={`borrar-recurso-${r.id}`} onClick={() => delRecurso(r.id)}>Borrar</button></td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "Avisos" && <AvisosPanel />}

      {tab === "Solicitudes" && <SolicitudesPanel />}

      {tab === "Moderación" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }}>
          <div>
            <h3 className="serif" style={{ fontSize: 18, marginBottom: 12 }}>Chat #general</h3>
            <table className="atable" data-testid="admin-chat"><tbody>
              {chat.map((m) => (<tr key={m.id}><td><b style={{ color: m.bot ? "var(--violet)" : "var(--gold)" }}>{m.nombre}</b><div>{m.texto}</div></td>
                <td style={{ width: 60 }}><button className="abtn danger" onClick={() => delChat(m.id)}>Borrar</button></td></tr>))}
            </tbody></table>
          </div>
          <div>
            <h3 className="serif" style={{ fontSize: 18, marginBottom: 12 }}>Muro</h3>
            <table className="atable" data-testid="admin-muro"><tbody>
              {posts.map((p) => (<tr key={p.id}><td><b style={{ color: "var(--gold)" }}>{p.nombre}</b><div>{p.texto}</div></td>
                <td style={{ width: 60 }}><button className="abtn danger" onClick={() => delPost(p.id)}>Borrar</button></td></tr>))}
              {posts.length === 0 && <tr><td style={{ color: "var(--muted)" }}>Sin publicaciones.</td></tr>}
            </tbody></table>
          </div>
        </div>
      )}

      {editCourse !== undefined && <CourseEditor curso={editCourse} onClose={() => setEditCourse(undefined)} onSaved={loadAll} />}
      {editCall !== undefined && <CallModal call={editCall} onClose={() => setEditCall(undefined)} onSaved={loadAll} />}
      {editPass && <PassModal alumno={editPass} onClose={() => setEditPass(null)} />}
      {editAcceso && <AccessModal alumno={editAcceso} cursos={cursos} onClose={() => setEditAcceso(null)} onSaved={loadAll} />}
      {nuevoAlumno && <NuevoAlumnoModal cursos={cursos} onClose={() => setNuevoAlumno(false)} onSaved={loadAll} />}
      {editAlumno && <EditAlumnoModal alumno={editAlumno} onClose={() => setEditAlumno(null)} onSaved={loadAll} />}
      {editEmpleo !== undefined && <EmpleoModal oferta={editEmpleo} onClose={() => setEditEmpleo(undefined)} onSaved={loadAll} />}
      {editRecurso !== undefined && <RecursoModal recurso={editRecurso} onClose={() => setEditRecurso(undefined)} onSaved={loadAll} />}
    </div></div>
  );
}
